import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Response, NotificationTemplateTypes } from '@ui/src';
import { NotificationTemplate, PermissionsAction } from '../types';
import { notificationSettingsApi } from './services';
import sectionActions from '../helpers/get-section-actions';

export interface TemplateColumnState {
  templates?: NotificationTemplate[];
  selectedTemplate?: NotificationTemplate;
  panelIsOpen: boolean;
  popupIsOpen: boolean;
  templateId?: string;
  sectionId: NotificationTemplateTypes;
  pageId?: NotificationTemplateTypes | 'all';
  isLoading?: boolean;
  search?: string;
  templateTypes?: NotificationTemplateTypes[];
  allActionsSectionFiltered?: PermissionsAction;
}

const initialState: TemplateColumnState = {
  panelIsOpen: false,
  popupIsOpen: false,
  sectionId: NotificationTemplateTypes.SECTION_MESSAGING,
  allActionsSectionFiltered: {},
};

export const templateColumnSlice = createSlice({
  name: 'templateColumns',
  initialState,
  reducers: {
    handleConfirmPopup: (state, action: PayloadAction<boolean>) => {
      state.popupIsOpen = action.payload;
    },
    setTemplates: (
      state,
      action: PayloadAction<{ name: string; id: string }[]>,
    ) => {
      state.templates = action.payload as any;
    },
    onSearch: (state, action: PayloadAction<string>) => {
      state.search = action.payload;
    },
    onTemplateChange: (state, action: PayloadAction<string>) => {
      const selectedRole =
        state.templates?.find((item) => item.id === action.payload) ||
        undefined;

      state.selectedTemplate = selectedRole;
      state.templateId = action.payload;
      state.pageId = undefined;
    },
    changePanelState: (state, action: PayloadAction<boolean>) => {
      state.panelIsOpen = action.payload;
    },
    onSectionChange: (
      state,
      action: PayloadAction<NotificationTemplateTypes>,
    ) => {
      state.sectionId = action.payload;
      state.allActionsSectionFiltered = action.payload
        ? sectionActions[action.payload]
        : {};
      state.search = '';
    },
    onPageChange: (
      state,
      action: PayloadAction<NotificationTemplateTypes | 'all' | undefined>,
    ) => {
      state.pageId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      notificationSettingsApi.endpoints.getNotificationsTemplates
        .matchFulfilled,
      (state, action: PayloadAction<Response<NotificationTemplate>>) => {
        state.templates = action.payload.data.edges.map((item) => ({
          ...item.node,
          notificationsTypes: item.node.notificationsTypes?.map(
            (notification) => `${notification}_ACTION`,
          ) as NotificationTemplateTypes[],
        }));
      },
    );
    builder.addMatcher(
      notificationSettingsApi.endpoints.getNotificationsTemplatesTypes
        .matchFulfilled,
      (state, action: PayloadAction<{ data: NotificationTemplateTypes[] }>) => {
        state.templateTypes = action.payload.data?.map(
          (item) => `${item}_ACTION`,
        ) as NotificationTemplateTypes[];
      },
    );
    builder.addMatcher(
      notificationSettingsApi.endpoints.createNotificationTemplate
        .matchFulfilled,
      (state, action: PayloadAction<{ data: NotificationTemplate }>) => {
        let nextTemplates: NotificationTemplate[] = [];

        if (state.templates) {
          nextTemplates = ([] as NotificationTemplate[]).concat(
            state.templates,
          );
        }

        nextTemplates.push(action.payload.data);

        state.templates = nextTemplates;
      },
    );
    builder.addMatcher(
      notificationSettingsApi.endpoints.editTemplate.matchFulfilled,
      (state, action: PayloadAction<{ data: NotificationTemplate }>) => {
        state.templates = state.templates?.map((item) => {
          let result = { ...item };

          if (result.id === action.payload.data.id) {
            result = {
              ...action.payload.data,
              notificationsTypes: action.payload.data.notificationsTypes?.map(
                (notification) => `${notification}_ACTION`,
              ) as NotificationTemplateTypes[],
            };
          }

          return result;
        });

        state.selectedTemplate = {
          ...action.payload.data,
          notificationsTypes: action.payload.data.notificationsTypes?.map(
            (notification) => `${notification}_ACTION`,
          ) as NotificationTemplateTypes[],
        };
      },
    );
  },
});

export const {
  onTemplateChange,
  setTemplates,
  changePanelState,
  onSectionChange,
  onPageChange,
  handleConfirmPopup,
  onSearch,
} = templateColumnSlice.actions;

export default templateColumnSlice.reducer;
